import React, { useState, useEffect } from 'react';

interface AuthenticatedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  fallback?: React.ReactNode;
}

const AuthenticatedImage: React.FC<AuthenticatedImageProps> = ({ 
  src, 
  alt, 
  fallback,
  ...props 
}): React.ReactElement => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let isMounted = true;

    if (!src) {
      setLoading(false);
      setError(true);
      return;
    }

    // Fetch image with credentials
    const fetchImage = async () => {
      try {
        // Handle relative URLs by prepending the API base URL
        let imageUrl = src;
        if (src.startsWith('/api/')) {
          const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
          // Remove /api from the base URL since src already contains it
          const baseUrl = apiBaseUrl.replace(/\/api$/, '');
          imageUrl = `${baseUrl}${src}`;
        }
        
        const response = await fetch(imageUrl, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`Failed to load image: ${response.status}`);
        }

        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        
        if (isMounted) {
          setImageSrc(objectUrl);
          setError(false);
        }
      } catch (err) {
        console.error('Error loading image:', err);
        if (isMounted) {
          setError(true);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchImage();

    // Cleanup
    return () => {
      isMounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src]);

  if (loading) {
    return (
      <div className={props.className} style={{ backgroundColor: '#f3f4f6' }}>
        <div className="animate-pulse h-full w-full" />
      </div>
    );
  }

  if (error || !imageSrc) {
    if (fallback) {
      return <>{fallback}</>;
    }
    return (
      <div className={props.className} style={{ backgroundColor: '#f3f4f6' }}>
        <svg className="h-12 w-12 text-gray-400 mx-auto my-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </div>
    );
  }

  return <img {...props} src={imageSrc} alt={alt} />;
};

export default AuthenticatedImage;